import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Save, Printer, FileText, FileSpreadsheet, ImageIcon, Plus, Edit2, Trash2, Copy, AlertTriangle,
  Calendar, ClipboardList, Info, ZoomIn, ZoomOut, Package, GitBranch, FileCheck, History,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { apqpService } from '../services/api';
import ApqpGanttChart from '../components/apqp/ApqpGanttChart';
import ApqpSamplingsTab from '../components/apqp/ApqpSamplingsTab';
import ApqpRevisionsTab from '../components/apqp/ApqpRevisionsTab';
import ApqpDocumentsTab from '../components/apqp/ApqpDocumentsTab';
import ApqpAuditTab from '../components/apqp/ApqpAuditTab';
import html2canvas from 'html2canvas';

const STATUSES = [
  'Da pianificare', 'Pianificato', 'In corso', 'In attesa del cliente', 'In attesa del fornitore',
  'Sospeso', 'In ritardo', 'Campionatura spedita', 'PPAP inviato', 'Approvato', 'Chiuso', 'Annullato',
];
const TASK_STATUSES = [
  'Non iniziata', 'Pianificata', 'In corso', 'Completata',
  'Prossima alla scadenza', 'In ritardo',
  'In attesa del cliente', 'In attesa del fornitore', 'Sospesa',
];
const TASK_PRIORITIES = ['Bassa', 'Media', 'Alta', 'Critica'];

const STATUS_COLOR = {
  'Non iniziata': 'bg-slate-100 text-slate-700 border-slate-300',
  'Pianificata': 'bg-blue-100 text-blue-800 border-blue-300',
  'In corso': 'bg-sky-100 text-sky-800 border-sky-300',
  'Completata': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Prossima alla scadenza': 'bg-amber-100 text-amber-800 border-amber-300',
  'In ritardo': 'bg-red-100 text-red-800 border-red-300',
  'In attesa del cliente': 'bg-purple-100 text-purple-800 border-purple-300',
  'In attesa del fornitore': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Sospesa': 'bg-neutral-200 text-neutral-800 border-neutral-400',
};

const defaultTask = () => ({
  name: '', description: '', task_type: 'task', parent_id: '',
  responsible: '', planned_start: '', planned_end: '',
  actual_start: '', actual_end: '', duration_days: 5,
  dependencies: [], progress: 0, status: 'Non iniziata',
  priority: 'Media', weight: 1.0, notes: '', delay_reason: '', order_index: 0,
});

export default function ApqpProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('generale');
  const [viewMode, setViewMode] = useState('Week');
  const [taskDialog, setTaskDialog] = useState({ open: false, editing: null, form: defaultTask() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apqpService.getProject(id);
      setProject(r.data);
      if (r.data?.view_preference) {
        setViewMode(r.data.view_preference === 'day' ? 'Day' : 'Week');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento progetto');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const savedField = async (patch) => {
    setSaving(true);
    try {
      const r = await apqpService.updateProject(id, patch);
      setProject(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    savedField({ view_preference: mode === 'Day' ? 'day' : 'week' });
  };

  // ─── Task CRUD ───
  const openNewTask = () => setTaskDialog({ open: true, editing: null, form: defaultTask() });
  const openEditTask = (task) => setTaskDialog({
    open: true, editing: task.id,
    form: {
      ...defaultTask(),
      ...task,
      parent_id: task.parent_id || '',
      dependencies: task.dependencies || [],
    },
  });

  const saveTask = async () => {
    const { editing, form } = taskDialog;
    if (!form.name?.trim()) { toast.error('Nome attività obbligatorio'); return; }
    const payload = { ...form };
    ['planned_start', 'planned_end', 'actual_start', 'actual_end'].forEach(k => {
      if (payload[k] === '') payload[k] = null;
    });
    if (payload.parent_id === '') payload.parent_id = null;
    payload.progress = Number(payload.progress || 0);
    payload.weight = Number(payload.weight || 1);
    payload.duration_days = payload.duration_days === '' ? null : Number(payload.duration_days);
    try {
      if (editing) {
        await apqpService.updateTask(editing, payload);
        toast.success('Attività aggiornata');
      } else {
        await apqpService.createTask(id, payload);
        toast.success('Attività aggiunta');
      }
      setTaskDialog(t => ({ ...t, open: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio attività');
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Eliminare l'attività "${task.name}"?`)) return;
    try {
      await apqpService.deleteTask(task.id);
      toast.success('Attività eliminata');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const duplicateTask = async (task) => {
    try {
      await apqpService.duplicateTask(task.id);
      toast.success('Attività duplicata');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore duplicazione');
    }
  };

  const onGanttDateChange = async (taskId, patch) => {
    try {
      await apqpService.updateTask(taskId, patch);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore modifica date');
    }
  };

  const onGanttProgress = async (taskId, progress) => {
    const t = project?.tasks?.find(x => x.id === taskId);
    if (!t) return;
    try {
      await apqpService.updateTask(taskId, { ...t, progress });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento avanzamento');
    }
  };

  const printPdf = (variant = 'internal') => {
    const url = apqpService.projectPdfUrl(id, variant);
    const token = localStorage.getItem('evotech_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => window.open(URL.createObjectURL(b), '_blank'))
      .catch(() => toast.error('Errore generazione PDF'));
  };

  const downloadExcel = () => {
    const url = apqpService.projectExcelUrl(id);
    const token = localStorage.getItem('evotech_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        if (!r.ok) throw new Error('http ' + r.status);
        return { blob: await r.blob(), fname: `APQP_${project.project_number}.xlsx` };
      })
      .then(({ blob, fname }) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fname;
        link.click();
        toast.success('Excel scaricato');
      })
      .catch(() => toast.error('Errore generazione Excel'));
  };

  const downloadGanttPng = async () => {
    if (tab !== 'gantt') { setTab('gantt'); toast.info('Apri il tab Gantt e riprova.'); return; }
    const el = document.querySelector('[data-testid="apqp-gantt-container"]');
    if (!el) { toast.error('Gantt non visibile. Apri il tab Gantt.'); return; }
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        scale: 2,          // hi-DPI for newsletter/report
        useCORS: true,
        logging: false,
      });
      canvas.toBlob((blob) => {
        if (!blob) { toast.error('Errore generazione PNG'); return; }
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Gantt_${project.project_number}.png`;
        link.click();
        toast.success('PNG Gantt scaricato');
      }, 'image/png');
    } catch (e) {
      toast.error('Errore generazione PNG: ' + (e?.message || 'sconosciuto'));
    }
  };

  if (loading || !project) {
    return (
      <>
        <div className="text-center text-slate-400 py-20">Caricamento…</div>
      </>
    );
  }

  const tasks = project.tasks || [];
  const potentialParents = tasks.filter(t => t.task_type === 'phase' && t.id !== taskDialog.editing);
  const potentialDeps = tasks.filter(t => t.id !== taskDialog.editing);

  return (
    <>
      <div className="space-y-4" data-testid="apqp-project-detail">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/apqp-projects')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Progetti
            </Button>
            <div>
              <div className="text-xs text-slate-500 font-mono">{project.project_number}</div>
              <h1 className="text-xl font-semibold text-slate-900">{project.title}</h1>
            </div>
            <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]">{project.project_type}</Badge>
            {(project.late_days || 0) > 0 && (
              <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]">
                <AlertTriangle className="w-3 h-3 mr-0.5 inline" /> +{project.late_days}gg ritardo
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500 mr-2">
              Avanzamento: <strong className="text-slate-800">{project.progress_percent || 0}%</strong>
            </div>
            <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            <Button variant="outline" onClick={() => printPdf('internal')} data-testid="apqp-pdf-btn"><Printer className="w-4 h-4 mr-1" /> PDF Interno</Button>
            <Button variant="outline" onClick={() => printPdf('customer')} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="apqp-pdf-customer-btn"><FileText className="w-4 h-4 mr-1" /> PDF Cliente</Button>
            <Button variant="outline" onClick={downloadExcel} className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="apqp-excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
            <Button variant="outline" onClick={downloadGanttPng} className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="apqp-png-btn"><ImageIcon className="w-4 h-4 mr-1" /> PNG Gantt</Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b flex-wrap">
          <TabBtn active={tab === 'generale'} onClick={() => setTab('generale')} icon={Info} label="Dati generali" testid="tab-generale" />
          <TabBtn active={tab === 'gantt'} onClick={() => setTab('gantt')} icon={Calendar} label="Gantt" testid="tab-gantt" />
          <TabBtn active={tab === 'attivita'} onClick={() => setTab('attivita')} icon={ClipboardList} label={`Attività (${tasks.length})`} testid="tab-attivita" />
          <TabBtn active={tab === 'campionature'} onClick={() => setTab('campionature')} icon={Package} label="Campionature" testid="tab-campionature" />
          <TabBtn active={tab === 'documentazione'} onClick={() => setTab('documentazione')} icon={FileCheck} label="Documentazione PPAP" testid="tab-documentazione" />
          <TabBtn active={tab === 'revisioni'} onClick={() => setTab('revisioni')} icon={GitBranch} label="Revisioni" testid="tab-revisioni" />
          <TabBtn active={tab === 'storico'} onClick={() => setTab('storico')} icon={History} label="Storico" testid="tab-storico" />
        </div>

        {tab === 'generale' && (
          <ProjectGeneralTab project={project} onSave={savedField} saving={saving} />
        )}

        {tab === 'campionature' && (
          <ApqpSamplingsTab projectId={id} />
        )}

        {tab === 'documentazione' && (
          <ApqpDocumentsTab projectId={id} tasks={tasks} />
        )}

        {tab === 'revisioni' && (
          <ApqpRevisionsTab projectId={id} />
        )}

        {tab === 'storico' && (
          <ApqpAuditTab projectId={id} />
        )}

        {tab === 'gantt' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Vista:</span>
                <div className="inline-flex rounded-md border overflow-hidden text-xs">
                  <button
                    className={`px-3 py-1 ${viewMode === 'Day' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                    onClick={() => changeViewMode('Day')}
                    data-testid="gantt-view-day"
                  ><ZoomIn className="w-3 h-3 mr-1 inline" /> Giornaliera</button>
                  <button
                    className={`px-3 py-1 ${viewMode === 'Week' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                    onClick={() => changeViewMode('Week')}
                    data-testid="gantt-view-week"
                  ><ZoomOut className="w-3 h-3 mr-1 inline" /> Settimanale</button>
                </div>
              </div>
              <Button size="sm" onClick={openNewTask} data-testid="add-task-btn"><Plus className="w-4 h-4 mr-1" /> Nuova attività</Button>
            </div>
            <ApqpGanttChart
              tasks={tasks}
              viewMode={viewMode}
              onTaskClick={openEditTask}
              onTaskUpdate={onGanttDateChange}
              onProgressChange={onGanttProgress}
            />
            <p className="text-xs text-slate-500">
              Suggerimento: trascina le barre per spostare le attività, i bordi per allungare/accorciare, clicca su una barra per modificarla.
            </p>
          </div>
        )}

        {tab === 'attivita' && (
          <div className="space-y-3">
            <div className="flex items-center justify-end">
              <Button size="sm" onClick={openNewTask}><Plus className="w-4 h-4 mr-1" /> Nuova attività</Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-left">
                        <th className="px-2 py-2 font-medium">Nome</th>
                        <th className="px-2 py-2 font-medium">Tipo</th>
                        <th className="px-2 py-2 font-medium">Responsabile</th>
                        <th className="px-2 py-2 font-medium">Inizio</th>
                        <th className="px-2 py-2 font-medium">Fine prev.</th>
                        <th className="px-2 py-2 font-medium">Fine eff.</th>
                        <th className="px-2 py-2 font-medium text-center">%</th>
                        <th className="px-2 py-2 font-medium">Stato</th>
                        <th className="px-2 py-2 font-medium">Priorità</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {tasks.length === 0 && (
                        <tr><td colSpan={10} className="text-center text-slate-400 py-6">Nessuna attività.</td></tr>
                      )}
                      {tasks.map(t => {
                        const isLate = t.planned_end && (Number(t.progress || 0) < 100)
                          && new Date(t.planned_end) < new Date();
                        return (
                          <tr key={t.id} className={t.task_type === 'phase' ? 'bg-slate-50 font-semibold' : ''} data-testid={`task-row-${t.id}`}>
                            <td className="px-2 py-1.5">
                              {t.task_type === 'phase' && <span className="text-slate-400 mr-1">▸</span>}
                              {t.task_type === 'milestone' && <span className="text-amber-600 mr-1">◆</span>}
                              {t.name}
                            </td>
                            <td className="px-2 py-1.5 text-[10px] uppercase text-slate-500">
                              {t.task_type === 'phase' ? 'Fase' : t.task_type === 'milestone' ? 'Milestone' : 'Attività'}
                            </td>
                            <td className="px-2 py-1.5">{t.responsible || '—'}</td>
                            <td className="px-2 py-1.5 font-mono text-[11px]">{t.planned_start || '—'}</td>
                            <td className={`px-2 py-1.5 font-mono text-[11px] ${isLate ? 'text-red-600 font-bold' : ''}`}>{t.planned_end || '—'}</td>
                            <td className="px-2 py-1.5 font-mono text-[11px]">{t.actual_end || '—'}</td>
                            <td className="px-2 py-1.5 text-center">{Math.round(Number(t.progress || 0))}%</td>
                            <td className="px-2 py-1.5"><Badge className={`${STATUS_COLOR[t.status] || 'bg-slate-100'} border text-[10px]`}>{t.status}</Badge></td>
                            <td className="px-2 py-1.5">{t.priority}</td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                              <Button variant="ghost" size="sm" onClick={() => openEditTask(t)} data-testid={`edit-task-${t.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => duplicateTask(t)} title="Duplica"><Copy className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteTask(t)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Task dialog */}
      <Dialog open={taskDialog.open} onOpenChange={(o) => !o && setTaskDialog(t => ({ ...t, open: false }))}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="apqp-task-dialog">
          <DialogHeader>
            <DialogTitle>{taskDialog.editing ? 'Modifica attività' : 'Nuova attività'}</DialogTitle>
            <DialogDescription>Compila i campi dell&apos;attività: nome, tipo, date e responsabile.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label>Nome *</Label>
              <Input value={taskDialog.form.name || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, name: e.target.value } }))} data-testid="task-name-input" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={taskDialog.form.task_type} onValueChange={v => setTaskDialog(t => ({ ...t, form: { ...t.form, task_type: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="phase">Fase</SelectItem>
                  <SelectItem value="task">Attività</SelectItem>
                  <SelectItem value="milestone">Milestone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {taskDialog.form.task_type !== 'phase' && (
              <div>
                <Label>Fase padre</Label>
                <Select value={taskDialog.form.parent_id || 'none'} onValueChange={v => setTaskDialog(t => ({ ...t, form: { ...t.form, parent_id: v === 'none' ? '' : v } }))}>
                  <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuna fase —</SelectItem>
                    {potentialParents.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Responsabile</Label>
              <Input value={taskDialog.form.responsible || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, responsible: e.target.value } }))} />
            </div>
            <div>
              <Label>Priorità</Label>
              <Select value={taskDialog.form.priority} onValueChange={v => setTaskDialog(t => ({ ...t, form: { ...t.form, priority: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data inizio</Label>
              <Input type="date" value={taskDialog.form.planned_start || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, planned_start: e.target.value } }))} />
            </div>
            <div>
              <Label>Data fine prevista</Label>
              <Input type="date" value={taskDialog.form.planned_end || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, planned_end: e.target.value } }))} />
            </div>
            <div>
              <Label>Data fine effettiva</Label>
              <Input type="date" value={taskDialog.form.actual_end || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, actual_end: e.target.value } }))} />
            </div>
            <div>
              <Label>Durata (gg)</Label>
              <Input type="number" value={taskDialog.form.duration_days ?? ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, duration_days: e.target.value } }))} />
            </div>
            <div>
              <Label>Avanzamento (%)</Label>
              <Input type="number" min={0} max={100} value={taskDialog.form.progress ?? 0} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, progress: e.target.value } }))} data-testid="task-progress-input" />
            </div>
            <div>
              <Label>Peso</Label>
              <Input type="number" step="0.1" value={taskDialog.form.weight ?? 1} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, weight: e.target.value } }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Stato</Label>
              <Select value={taskDialog.form.status} onValueChange={v => setTaskDialog(t => ({ ...t, form: { ...t.form, status: v } }))}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dipendenze</Label>
              <Select
                value=""
                onValueChange={v => {
                  if (!v) return;
                  setTaskDialog(t => {
                    const deps = new Set(t.form.dependencies || []);
                    deps.add(v);
                    return { ...t, form: { ...t.form, dependencies: Array.from(deps) } };
                  });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Aggiungi dipendenza…" /></SelectTrigger>
                <SelectContent>{potentialDeps.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
              {(taskDialog.form.dependencies || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {taskDialog.form.dependencies.map(depId => {
                    const dep = tasks.find(x => x.id === depId);
                    return (
                      <Badge
                        key={depId}
                        className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px] cursor-pointer"
                        onClick={() => setTaskDialog(t => ({ ...t, form: { ...t.form, dependencies: t.form.dependencies.filter(x => x !== depId) } }))}
                        title="Clicca per rimuovere"
                      >
                        {dep ? dep.name : depId} ×
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="md:col-span-3">
              <Label>Note</Label>
              <Input value={taskDialog.form.notes || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, notes: e.target.value } }))} />
            </div>
            {(Number(taskDialog.form.progress) || 0) < 100 && taskDialog.form.planned_end && new Date(taskDialog.form.planned_end) < new Date() && (
              <div className="md:col-span-3">
                <Label>Motivo del ritardo</Label>
                <Input value={taskDialog.form.delay_reason || ''} onChange={e => setTaskDialog(t => ({ ...t, form: { ...t.form, delay_reason: e.target.value } }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialog(t => ({ ...t, open: false }))}>Annulla</Button>
            <Button onClick={saveTask} data-testid="save-task-btn"><Save className="w-4 h-4 mr-1" /> Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${active ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
    data-testid={testid}
  >
    <Icon className="w-4 h-4 mr-1.5 inline" /> {label}
  </button>
);

const ProjectGeneralTab = ({ project, onSave, saving }) => {
  const [f, setF] = useState({
    title: project.title || '',
    drawing_code: project.drawing_code || '', drawing_revision: project.drawing_revision || '',
    ppap_quantity: project.ppap_quantity ?? '',
    project_type: project.project_type || 'APQP + PPAP',
    ppap_level: project.ppap_level || '',
    internal_manager: project.internal_manager || '',
    customer_contact: project.customer_contact || '',
    customer_requested_date: project.customer_requested_date || '',
    planned_sampling_date: project.planned_sampling_date || '',
    planned_documentation_date: project.planned_documentation_date || '',
    planned_approval_date: project.planned_approval_date || '',
    status: project.status || 'Da pianificare',
    notes: project.notes || '',
  });
  useEffect(() => {
    setF({
      title: project.title || '',
      drawing_code: project.drawing_code || '', drawing_revision: project.drawing_revision || '',
      ppap_quantity: project.ppap_quantity ?? '',
      project_type: project.project_type || 'APQP + PPAP',
      ppap_level: project.ppap_level || '',
      internal_manager: project.internal_manager || '',
      customer_contact: project.customer_contact || '',
      customer_requested_date: project.customer_requested_date || '',
      planned_sampling_date: project.planned_sampling_date || '',
      planned_documentation_date: project.planned_documentation_date || '',
      planned_approval_date: project.planned_approval_date || '',
      status: project.status || 'Da pianificare',
      notes: project.notes || '',
    });
  }, [project]);

  const submit = () => {
    const payload = { ...f };
    if (payload.ppap_quantity === '') payload.ppap_quantity = null;
    else payload.ppap_quantity = Number(payload.ppap_quantity);
    onSave(payload);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2"><Label>Titolo</Label><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></div>
          <div>
            <Label>Stato</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Cliente</Label><Input value={project.client_name || '—'} readOnly /></div>
          <div><Label>Codice prodotto</Label><Input value={project.product_code || '—'} readOnly /></div>
          <div><Label>Descrizione prodotto</Label><Input value={project.product_description || '—'} readOnly /></div>
          <div><Label>Disegno</Label><Input value={f.drawing_code} onChange={e => setF({ ...f, drawing_code: e.target.value })} /></div>
          <div><Label>Revisione</Label><Input value={f.drawing_revision} onChange={e => setF({ ...f, drawing_revision: e.target.value })} /></div>
          <div><Label>Q.tà campionatura</Label><Input type="number" value={f.ppap_quantity} onChange={e => setF({ ...f, ppap_quantity: e.target.value })} /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={f.project_type} onValueChange={(v) => setF({ ...f, project_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['APQP', 'PPAP', 'APQP + PPAP'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Livello PPAP</Label>
            <Select value={f.ppap_level || 'none'} onValueChange={(v) => setF({ ...f, ppap_level: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {['1','2','3','4','5'].map(l => <SelectItem key={l} value={l}>Livello {l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Responsabile interno</Label><Input value={f.internal_manager} onChange={e => setF({ ...f, internal_manager: e.target.value })} /></div>
          <div><Label>Referente cliente</Label><Input value={f.customer_contact} onChange={e => setF({ ...f, customer_contact: e.target.value })} /></div>
          <div><Label>Data richiesta cliente</Label><Input type="date" value={f.customer_requested_date || ''} onChange={e => setF({ ...f, customer_requested_date: e.target.value })} /></div>
          <div><Label>Data campionatura</Label><Input type="date" value={f.planned_sampling_date || ''} onChange={e => setF({ ...f, planned_sampling_date: e.target.value })} /></div>
          <div><Label>Data invio PPAP</Label><Input type="date" value={f.planned_documentation_date || ''} onChange={e => setF({ ...f, planned_documentation_date: e.target.value })} /></div>
          <div><Label>Data approvazione</Label><Input type="date" value={f.planned_approval_date || ''} onChange={e => setF({ ...f, planned_approval_date: e.target.value })} /></div>
          <div className="md:col-span-3"><Label>Note</Label><Input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={saving} data-testid="save-project-btn"><Save className="w-4 h-4 mr-1" /> Salva</Button>
        </div>
      </CardContent>
    </Card>
  );
};
